import type { Except } from "type-fest";
import { z } from "zod";
import type { OptionalId} from "../optionals/optional-id";
import { optionalIdZodSchema } from "../optionals/optional-id";
import type {
    OrderItemId,
    OrderItemId_V1,
    OrderItemId_V2} from "../orders/order-items/order-item-id";
import {
    orderItemIdZodSchema,
    orderItemIdZodSchema_V1,
} from "../orders/order-items/order-item-id";
import type { Price} from "../prices/price";
import { priceZodSchema } from "../prices/price";
import type { ProductId} from "../products/product-id";
import { productIdZodSchema } from "../products/product-id";
import type {
    Translatable} from "../translations/translation";
import {
    translatableZodSchema,
} from "../translations/translation";
import type {
    TransactionItemId} from "./transaction-item-id";
import {
    transactionItemIdFactory,
    transactionItemIdZodSchema,
} from "./transaction-item-id";

type TransactionItem = {
    readonly _id: TransactionItemId;
    /**
     * Doesn't exist on old transaction items, pre mid-may 2022 approximately
     * @type {"TRANSACTION_ITEM"}
     */
    readonly _type?: "TRANSACTION_ITEM";
    readonly orderItemId: OrderItemId_V2 | OrderItemId_V1 | ""; //To be able to group
    readonly title: Translatable;
    readonly pricePerItem: Price;
    readonly quantity: number;
    readonly productId?: ProductId;
    readonly parentProductId?: ProductId; //To find parent product
    readonly optionalId?: OptionalId;
    readonly parentItemId?: OrderItemId; //To find parent item
};

type FactoryParams = Except<TransactionItem, "_id"> & {
    _id?: TransactionItemId;
};

const transactionItemZodSchema: z.ZodSchema<TransactionItem> = z.object({
    _id: transactionItemIdZodSchema,
    _type: z
        .literal("TRANSACTION_ITEM")
        .optional()
        .transform(
            () => "TRANSACTION_ITEM"
        ) as unknown as z.ZodSchema<"TRANSACTION_ITEM">,
    orderItemId: orderItemIdZodSchema
        .or(orderItemIdZodSchema_V1)
        .or(z.literal("")),
    title: translatableZodSchema,
    pricePerItem: priceZodSchema,
    quantity: z.number(),
    productId: productIdZodSchema.optional(),
    parentProductId: productIdZodSchema.optional(),
    optionalId: optionalIdZodSchema.optional(),
    parentItemId: orderItemIdZodSchema.optional(),
});

const transactionItemFactory = (
    params: Except<FactoryParams, "parentItemId"> & {
        parentItemId: OrderItemId | undefined;
    }
): TransactionItem => {
    const transactionItem: TransactionItem = transactionItemZodSchema.parse({
        _id: transactionItemIdFactory(),
        ...params,
    });
    return transactionItem;
};

export { transactionItemFactory, transactionItemZodSchema };
export type { TransactionItem };
